import * as privateEnv from '~config/privateEnv';
import * as publicEnv from '~config/publicEnv';
import { IS_SSR } from '~constants/common';

import { ErrorResult, HttpClient, RequestParams } from '~utils/https/HttpClientFetch';

import type {
  BookingResponse,
  CreateBookingParams,
  MemberMutationResponse,
  MRTMemberInfo,
  OptionListParams,
  PlaceListResponse,
  PointToPointOfferListResponse,
  PointToPointTicketListResponse,
  PointToPointTicketCursorListResponse,
  PopularPlaceListResponse,
  PopularPlaceType,
  PopularRouteListResponse,
  PostTrpPrebookParams,
  PostTrpPrebookResponse,
  PriceCalendarParams,
  PriceCalendarResponse,
  SearchPointToPointTicketParam,
  TicketDetailParams,
  TicketDetailResponse,
  TravelersResponse,
  TrpOrderDetailResponse,
  UpdateBoardingInfoParams,
  TransportationProductsResponse,
} from '~models/Transportation/PointToPointTicket';

/**
 * @title Transportation API
 * @baseUrl https://api3.myreaptrip.com/transportation
 *
 */
class TransportationApi<SecurityDataType> extends HttpClient<SecurityDataType> {
  v1 = {
    RailEurope: {
      /**
       * @description 유럽 열차 출도착지 목록 조회
       *
       * @tags Transportation > RailEurope > PlaceList
       * @name GetPlaceList
       * @request GET:/api/train/raileurope/places
       */
      getPlaceList: (keyword: string, page?: number, size?: number) =>
        this.request<PlaceListResponse, ErrorResult>({
          path: '/api/train/raileurope/places',
          method: 'GET',
          query: {
            keyword,
            page,
            size,
          },
        }),
      /**
       * @description 인기 출발지 장소 목록 조회
       *
       * @tags Transportation > RailEurope > Places > Popular
       * @name GetPopularPlaceList
       * @request GET: /api/train/raileurope/popular
       */
      getPopularPlaceList: (type: PopularPlaceType) =>
        this.request<PopularPlaceListResponse, ErrorResult>({
          path: `/api/train/raileurope/places/popular`,
          method: 'GET',
          query: {
            type,
          },
        }),
      /**
       * @description 인기 노선 목록 조회
       *
       * @tags Transportation > RailEurope > Places > Popular > Route
       * @name GetPopularRouteList
       * @request GET: /api/train/raileurope/popular/route
       */
      getPopularRouteList: () =>
        this.request<PopularRouteListResponse, ErrorResult>({
          path: `/api/train/raileurope/places/popular/route`,
          method: 'GET',
        }),
      /**
       * @description 가격 캘린더 정보 조회
       *
       * @tags Transportation > RailEurope > Price Calendar
       * @name GetPriceCalendar
       * @request POST: /api/train/raileurope/calendars
       */
      getPriceCalendar: (params: PriceCalendarParams) =>
        this.request<PriceCalendarResponse, ErrorResult>({
          path: `/api/train/raileurope/calendars`,
          method: 'POST',
          body: params,
        }),
      /**
       * @description 유럽 열차 검색 요청
       *
       * @tags Transportation > RailEurope > Search
       * @name SearchPointToPointTicket
       * @summary 유럽 열차 구간권 검색 API
       * @request POST: /api/train/raileurope/v2/search
       */
      search: (params: SearchPointToPointTicketParam, signal?: AbortSignal) =>
        this.request<PointToPointTicketListResponse, ErrorResult>({
          path: '/api/train/raileurope/v2/search',
          method: 'POST',
          body: params,
          baseURL: publicEnv.NEXT_PUBLIC_TRANSPORTATION_LONG_TIMEOUT_API_ENDPOINT,
          signal,
        }),
      /**
       * @description 유럽 열차 검색 결과 고유 Id로 첫번째 결과 목록 조회
       *
       * @tags Transportation > RailEurope > Search
       * @name GetLegSolutionFirstList
       * @request GET: /api/train/raileurope/v2/search/{correlationId}/init
       */
      getLegSolutionFirstList: ({
        correlationId,
        outboundOfferId,
      }: {
        correlationId: string;
        outboundOfferId?: string;
      }) => {
        const queryParams = {
          correlationId,
          ...(outboundOfferId ? { outboundOfferId } : {}),
        };
        return this.request<PointToPointTicketListResponse, ErrorResult>({
          path: `/api/train/raileurope/v2/search/${correlationId}/init`,
          method: 'GET',
          query: queryParams,
        });
      },
      /**
       * @description 유럽 열차 검색 결과 고유 Id로 두번째 이후 결과 목록 조회
       *
       * @tags Transportation > RailEurope > Search
       * @name GetLegSolutionRestList
       * @request GET: /api/train/raileurope/v2/search/{correlationId}/additional
       */
      getLegSolutionRestList: ({
        correlationId,
        searchId,
        outboundOfferId,
        signal,
      }: {
        correlationId: string;
        searchId: string;
        outboundOfferId?: string;
        signal?: AbortSignal;
      }) => {
        return this.request<PointToPointTicketCursorListResponse, ErrorResult>({
          path: `/api/train/raileurope/v2/search/${correlationId}/additional`,
          method: 'GET',
          query: {
            correlationId,
            searchId,
            ...(outboundOfferId && { outboundOfferId }),
          },
          baseURL: publicEnv.NEXT_PUBLIC_TRANSPORTATION_LONG_TIMEOUT_API_ENDPOINT,
          signal,
        });
      },
      /**
       * @description 선택한 leg solution id로 옵션 목록 조회
       *
       * @tags Transportation > RailEurope > Search
       * @name GetOptionList
       * @request GET: /api/train/raileurope/v2/search/{correlationId}/options
       */
      getOptionList: (params: OptionListParams) =>
        this.request<PointToPointOfferListResponse, ErrorResult>({
          path: `/api/train/raileurope/v2/search/${params.correlationId}/options`,
          method: 'GET',
          query: params,
        }),
      /**
       * @description 선택한 offer 기반 티켓 상세 정보 조회
       *
       * @tags Transportation > RailEurope > TicketDetail
       * @name TicketDetail
       * @request POST: /api/train/raileurope/v2/search/{correlationId}/details
       */
      getTicketDetail: (params: TicketDetailParams) =>
        this.request<TicketDetailResponse, ErrorResult>({
          path: `/api/train/raileurope/v2/search/${params.correlationId}/details`,
          method: 'GET',
          query: params,
        }),
      /**
       * @description 선택한 offer 기반 booking 생성
       *
       * @tags Transportation > RailEurope > Booking > Create
       * @name CreateBooking
       * @request POST: /api/train/raileurope/booking/create
       */
      createBooking: (params: CreateBookingParams) =>
        this.request<BookingResponse, ErrorResult>({
          path: '/api/train/raileurope/booking/create',
          method: 'POST',
          body: params,
          headers: {
            'X-CorrelationId': params.correlationId,
          },
        }),
      /**
       * @description trpOrderId 기반 탑승자 정보 조회
       *
       * @tags Transportation > RailEurope > Travelers
       * @name GetTravelers
       * @request GET: /api/train/raileurope/travelers/{trpOrderId}
       */
      getBoardingInfo: (trpOrderId: string, params: RequestParams = {}) =>
        this.request<TravelersResponse, ErrorResult>({
          path: `/api/train/raileurope/travelers/${trpOrderId}`,
          method: 'GET',
          ...params,
        }),
      /**
       * @description 탑승자 정보 등록
       *
       * @tags Transportation > RailEurope > Travelers
       * @name UpdateBoardingInfo
       * @request PUT: /api/train/raileurope/travelers/{trpOrderId}
       */
      updateBoardingInfo: (trpOrderId: string, params: UpdateBoardingInfoParams) =>
        this.request<BookingResponse, ErrorResult>({
          path: `/api/train/raileurope/travelers/${trpOrderId}`,
          method: 'PUT',
          body: params,
        }),
      /**
       * @description 여행자(member) 정보 생성
       *
       * @tags Transportation > Member > Traveler
       * @name CreateMember
       * @request POST: /api/member/traveler-infos
       */
      createMember: (params: MRTMemberInfo) =>
        this.request<MemberMutationResponse, ErrorResult>({
          path: '/api/member/traveler-infos',
          method: 'POST',
          body: params,
        }),
      /**
       * @description 여행자(member) 정보 수정
       *
       * @tags Transportation > Member > Traveler
       * @name UpdateMember
       * @request PUT: /api/member/traveler-infos
       */
      updateMember: (params: MRTMemberInfo) =>
        this.request<MemberMutationResponse, ErrorResult>({
          path: '/api/member/traveler-infos',
          method: 'PATCH',
          body: params,
        }),
      /**
       * @description 여행자(member) 정보 삭제
       *
       * @tags Transportation > Member > Traveler
       * @name DeleteMember
       * @request DELETE: /api/member/traveler-infos/{userId}
       */
      deleteMember: (userId: string) =>
        this.request<MemberMutationResponse, ErrorResult>({
          path: `/api/member/traveler-infos/${userId}`,
          method: 'DELETE',
        }),
      /**
       * @description 가주문서 생성 요청을 위한 사전 정보 조회
       *
       * @tags Transportation > RailEurope > Booking > PreBook
       * @name GetTrpOrderDetail
       * @request GET: /api/train/raileurope/booking/{trpOrderId}
       */
      getTrpOrderDetail: (trpOrderId: string) =>
        this.request<TrpOrderDetailResponse, ErrorResult>({
          path: `/api/train/raileurope/booking/${trpOrderId}`,
          method: 'GET',
        }),
      /**
       * @description 가주문서 생성 이후 레일유럽에 Prebook 요청
       *
       * @tags Transportation > RailEurope > Booking > PreBook
       * @name PostTrpPrebook
       * @request POST: /api/train/raileurope/booking/{trpOrderId}/prebook
       */
      postTrpPrebook: ({ correlationId, trpOrderId, orderFormId }: PostTrpPrebookParams) =>
        this.request<PostTrpPrebookResponse, ErrorResult>({
          path: `/api/train/raileurope/booking/${trpOrderId}/prebook`,
          method: 'POST',
          body: {
            correlationId,
            orderFormId,
          },
        }),
      /**
       * @description Transport Product Aggregated 정보 조회
       *
       * @tags Transportation > TransportProduct > Products
       * @name GetTransportProducts
       * @request GET: /api/products
       */
      getTransportProducts: () =>
        this.request<TransportationProductsResponse>({
          path: '/api/products',
          method: 'GET',
        }),
    },
  };
}

const transportationApi = new TransportationApi({
  baseURL: IS_SSR ? privateEnv.TRANSPORTATION_API_ENDPOINT : publicEnv.TRANSPORTATION_API_ENDPOINT,
  secureWorker(securityData) {
    return {
      headers: {
        Authorization: `Bearer ${securityData}`,
      },
    };
  },
  secure: false,
  defaultHeaders: {
    accept: 'application/json',
  },
});

export { transportationApi };
